import JSONbig from 'json-bigint';

const DEFAULT_TTL = 24 * 60 * 60 * 1000; // 24 hours for seldom-changing data
const PREFIX = 'cache_'; // To avoid conflicts with other localStorage keys

// Configure JSONbig to handle native BigInt serialization
const JSONbigString = JSONbig({
  useNativeBigInt: true,
  alwaysParseAsBig: false,
  protoAction: 'ignore',
  constructorAction: 'ignore'
});

// Memory cache (for performance)
const memoryCache = new Map();

export default {
  /**
   * Set cache with optional TTL (persists across refreshes)
   * @param {string} key - Cache key
   * @param {any} value - Value to cache
   * @param {number} ttl - Time to live in ms (optional)
   */
  set(key, value, ttl = DEFAULT_TTL) {
    const expiresAt = Date.now() + ttl;
    const data = { value, expiresAt };
    
    // Store in both memory and localStorage
    memoryCache.set(key, data);
    try {
      localStorage.setItem(`${PREFIX}${key}`, JSONbigString.stringify(data));
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        console.warn('LocalStorage quota exceeded, falling back to memory cache');
      } else {
        console.warn('LocalStorage set error:', e);
      }
    }
    return value;
  },

  /**
   * Get cached value if exists and not expired
   * @param {string} key - Cache key
   * @returns {any|null} Cached value or null
   */
  get(key) {
    // Check memory cache first (faster)
    const memoryItem = memoryCache.get(key);
    if (memoryItem) {
      if (Date.now() > memoryItem.expiresAt) {
        this.delete(key);
        return null;
      }
      return this._convertBigInt(memoryItem.value);
    }

    // Fallback to localStorage
    try {
      const storedItem = localStorage.getItem(`${PREFIX}${key}`);
      if (storedItem) {
        const parsed = JSONbigString.parse(storedItem);
        const { value, expiresAt } = parsed;
        
        // Check expiration
        if (Date.now() > expiresAt) {
          this.delete(key);
          return null;
        }
        
        // Rehydrate memory cache
        const convertedValue = this._convertBigInt(value);
        memoryCache.set(key, { value: convertedValue, expiresAt });
        return convertedValue;
      }
    } catch (e) {
      console.warn('Error reading from localStorage:', e);
      this.delete(key); // Auto-clean corrupted entries
    }
    
    return null;
  },

  /**
   * Convert BigInt strings back to proper BigInt objects
   * @private
   */
  // eslint-disable-next-line
  _convertBigInt(value) {
    if (Array.isArray(value)) {
      return value.map(item => this._convertBigInt(item));
    } else if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value).map(([k, v]) => [k, this._convertBigInt(v)])
      );
    } else if (typeof value === 'string' && /^\d+n$/.test(value)) {
      // eslint-disable-next-line
      return BigInt(value.slice(0, -1));
    }
    return value;
  },

  /**
   * Delete specific cache entry
   * @param {string} key - Cache key to delete
   */
  delete(key) {
    memoryCache.delete(key);
    try {
      localStorage.removeItem(`${PREFIX}${key}`);
    } catch (e) {
      console.warn('Error deleting from localStorage:', e);
    }
  },

  /**
   * Clear entire cache (both memory and persistent)
   */
  clear() {
    memoryCache.clear();
    try {
      // Only remove our prefixed items
      Object.keys(localStorage).forEach(k => {
        if (k.startsWith(PREFIX)) {
          localStorage.removeItem(k);
        }
      });
    } catch (e) {
      console.warn('Error clearing localStorage cache:', e);
    }
  },

  /**
   * Get cache keys (for debugging)
   */
  keys() {
    try {
      return Array.from(new Set([
        ...memoryCache.keys(),
        ...Object.keys(localStorage)
          .filter(k => k.startsWith(PREFIX))
          .map(k => k.slice(PREFIX.length))
      ]));
    } catch (e) {
      console.warn('Error retrieving cache keys:', e);
      return Array.from(memoryCache.keys());
    }
  }
};